import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon, BoltIcon, ChartBarIcon, DocumentTextIcon,
  UserGroupIcon, PlusIcon, CheckCircleIcon, ShoppingBagIcon
} from '@heroicons/react/24/outline';
import { formatCurrencyInput, parseCurrencyInput } from '../utils/currencyUtils';

const DEMO_MEMBERS = [
  { name: 'Budi', avatar: 'https://api.dicebear.com/9.x/micah/svg?seed=Budi' },
  { name: 'Andi', avatar: 'https://api.dicebear.com/9.x/micah/svg?seed=Andi' },
  { name: 'Cici', avatar: 'https://api.dicebear.com/9.x/micah/svg?seed=Cici' }
];

const Landing = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [demoGroupName, setDemoGroupName] = useState('');
  const [demoBill, setDemoBill] = useState({ desc: '', amount: '' });
  const [demoPaidBy, setDemoPaidBy] = useState(0);
  const [demoSplitMembers, setDemoSplitMembers] = useState([0, 1, 2]);

  const nextStep = () => setStep((s) => Math.min(s + 1, 4));
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const toggleSplit = (idx) => {
    setDemoSplitMembers((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const splitAmount = demoBill.amount && demoSplitMembers.length > 0
    ? Math.round(parseFloat(demoBill.amount) / demoSplitMembers.length)
    : 0;

  const settlements = [];
  if (splitAmount > 0) {
    DEMO_MEMBERS.forEach((m, idx) => {
      if (idx !== demoPaidBy && demoSplitMembers.includes(idx)) {
        settlements.push({ from: m.name, to: DEMO_MEMBERS[demoPaidBy].name, amount: splitAmount });
      }
    });
  }

  const features = [
    { icon: BoltIcon, title: 'Cepat', description: 'Catat transaksi dalam hitungan detik, otomatis terbagi rata' },
    { icon: ChartBarIcon, title: 'Transparan', description: 'Semua member bisa lihat siapa hutang berapa, real-time' },
    { icon: DocumentTextIcon, title: 'Laporan Otomatis', description: 'Sistem hitung sendiri siapa bayar ke siapa, tinggal settle up' }
  ];

  const fmt = (n) => 'Rp ' + n.toLocaleString('id-ID');

  const stepLabels = ['Buat Grup', 'Pilih Anggota', 'Input Tagihan', 'Lihat Hasil', 'Settlement'];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Hero */}
      <div className="bg-surface rounded-b-[40px] relative overflow-hidden mb-10 pb-40 pt-20 border-b border-border">
        {/* Decorative elements */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-80 h-80 bg-blue-400/10 rounded-full blur-3xl"></div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col-reverse md:flex-row items-center justify-between text-center md:text-left pt-6 md:pt-24 px-6 max-w-6xl mx-auto relative z-10 gap-10 md:gap-0">
          <div className="flex-1 max-w-lg mb-12 md:mb-0">
            <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mb-6 leading-[1.1]">
              Patungan <br /><span className="text-primary text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-500">Gak Pake Pusing.</span>
            </motion.h1>
            <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 mb-8 max-w-md mx-auto md:mx-0 leading-relaxed">
              Catat pengeluaran bersama, hitung pembagian otomatis, dan selesaikan tagihan teman tanpa rasa segan.
            </motion.p>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
              <button
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto px-8 py-4 bg-primary text-white font-bold rounded-full hover:bg-primary-hover active:scale-95 transition-all shadow-float flex items-center justify-center gap-2"
              >
                Mulai Sekarang <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('demo-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 font-bold rounded-full hover:bg-slate-50 border border-slate-200 active:scale-95 transition-all"
              >
                Coba Demo
              </button>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
            className="flex-1 flex justify-center md:justify-end w-full max-w-[280px] sm:max-w-sm md:max-w-none relative z-10">
            <img src="/credit-card-payments.svg" alt="Patungin Illustration" className="w-full h-auto drop-shadow-xl md:max-w-[450px]" />
          </motion.div>
        </motion.div>
      </div>

      {/* Live Demo - Interactive Flow */}
      <motion.div
        id="demo-section"
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ type: 'spring', damping: 20 }}
        className="bg-white p-6 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-slate-100 w-full max-w-[90%] sm:max-w-md mx-auto -mt-44 relative z-20 mb-16 overflow-hidden"
      >
        {/* Demo Badge */}
        <div className="absolute top-0 right-0 bg-amber-100/80 backdrop-blur-sm text-amber-700 text-[10px] font-bold px-3 py-1.5 rounded-bl-2xl flex items-center gap-1 shadow-sm border-b border-l border-amber-200/50">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
          HANYA DEMO
        </div>
        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6 pt-5">
          {stepLabels.map((label, idx) => (
            <div key={idx} className="flex flex-col items-center" onClick={() => setStep(idx)}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${idx === step ? 'bg-primary text-white shadow-float scale-110'
                : idx < step ? 'bg-success text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                {idx < step ? <CheckCircleIcon className="w-5 h-5" /> : idx + 1}
              </div>
              <span className={`text-[9px] mt-1 font-medium hidden sm:block ${idx === step ? 'text-primary' : 'text-slate-400'}`}>{label}</span>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 0: Create Group */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Buat Grup Patungan</h3>
              <p className="text-sm text-slate-500 mb-4">Mulai dengan nama grup</p>
              <input type="text" data-testid="demo-group-name" value={demoGroupName}
                onChange={(e) => setDemoGroupName(e.target.value)}
                placeholder="Misal: Trip Jogja, Geng Bakso..."
                className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all mb-4" />
              <button onClick={nextStep} data-testid="demo-next-0" disabled={!demoGroupName.trim()}
                className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-40 disabled:cursor-not-allowed">
                Lanjut
              </button>
            </motion.div>
          )}

          {/* Step 1: Select Members */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Anggota Grup</h3>
              <p className="text-sm text-slate-500 mb-4">Ini teman-teman dalam <span className="font-semibold text-primary">{demoGroupName || 'grup'}</span> kamu</p>
              <div className="flex justify-center gap-6 mb-6">
                {DEMO_MEMBERS.map((m, i) => (
                  <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.1, type: 'spring' }}
                    className="flex flex-col items-center gap-2">
                    <img src={m.avatar} alt={m.name} className="w-16 h-16 rounded-full border-3 border-primary/20 shadow-soft" />
                    <span className="text-sm font-semibold text-slate-800">{m.name}</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={prevStep} className="flex-1 bg-surface text-slate-600 py-3 rounded-full font-semibold hover:bg-surface-hover transition-all">Kembali</button>
                <button onClick={nextStep} data-testid="demo-next-1" className="flex-1 bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float">Lanjut</button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Add Bill */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Tambah Tagihan</h3>
              <p className="text-sm text-slate-500 mb-4">Siapa bayar apa?</p>
              <div className="space-y-3">
                <input type="text" data-testid="demo-bill-desc" value={demoBill.desc}
                  onChange={(e) => setDemoBill({ ...demoBill, desc: e.target.value })}
                  placeholder="Deskripsi: Makan Bakso..."
                  className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />
                <input type="text" inputMode="numeric" data-testid="demo-bill-amount" value={formatCurrencyInput(demoBill.amount)}
                  onChange={(e) => setDemoBill({ ...demoBill, amount: parseCurrencyInput(e.target.value) })}
                  placeholder="Nominal: 150.000"
                  className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />

                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">Dibayar oleh:</p>
                  <div className="flex gap-2">
                    {DEMO_MEMBERS.map((m, i) => (
                      <button key={i} type="button" onClick={() => setDemoPaidBy(i)} data-testid={`demo-paidby-${i}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${demoPaidBy === i ? 'border-primary bg-primary text-white' : 'border-border text-slate-600'
                          }`}>
                        <img src={m.avatar} alt={m.name} className="w-5 h-5 rounded-full" />
                        {m.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">Dibagi ke:</p>
                  <div className="flex gap-2">
                    {DEMO_MEMBERS.map((m, i) => (
                      <button key={i} type="button" onClick={() => toggleSplit(i)} data-testid={`demo-split-${i}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${demoSplitMembers.includes(i) ? 'border-primary bg-primary text-white' : 'border-border text-slate-600'
                          }`}>
                        <img src={m.avatar} alt={m.name} className="w-5 h-5 rounded-full" />
                        {m.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={prevStep} className="flex-1 bg-surface text-slate-600 py-3 rounded-full font-semibold hover:bg-surface-hover transition-all">Kembali</button>
                <button onClick={nextStep} data-testid="demo-next-2" disabled={!demoBill.desc || !demoBill.amount}
                  className="flex-1 bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-40 disabled:cursor-not-allowed">
                  Lanjut
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Result */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Ringkasan Tagihan</h3>
              <p className="text-sm text-slate-500 mb-4">Otomatis dihitung Patungin!</p>

              <div className="bg-surface p-4 rounded-3xl mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <ShoppingBagIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{demoBill.desc || 'Makan Bakso'}</p>
                    <p className="text-xs text-slate-500">Dibayar oleh {DEMO_MEMBERS[demoPaidBy].name}</p>
                  </div>
                  <p className="text-lg font-bold text-slate-900">{fmt(parseFloat(demoBill.amount) || 0)}</p>
                </div>
                <div className="border-t border-border pt-3">
                  <p className="text-xs text-slate-500 mb-2">Per orang bayar:</p>
                  <div className="flex items-center justify-center gap-2">
                    {DEMO_MEMBERS.filter((_, i) => demoSplitMembers.includes(i)).map((m, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <img src={m.avatar} alt={m.name} className="w-8 h-8 rounded-full" />
                        <span className="text-[10px] font-medium text-slate-600">{m.name}</span>
                        <span className="text-xs font-bold text-primary" data-testid={`demo-split-result-${i}`}>{fmt(splitAmount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={prevStep} className="flex-1 bg-surface text-slate-600 py-3 rounded-full font-semibold hover:bg-surface-hover transition-all">Kembali</button>
                <button onClick={nextStep} data-testid="demo-next-3" className="flex-1 bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float">Lihat Settlement</button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Settlement */}
          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Siapa Bayar ke Siapa?</h3>
              <p className="text-sm text-slate-500 mb-4">Satu klik untuk settle up!</p>

              <div className="space-y-3 mb-4">
                {settlements.length > 0 ? settlements.map((s, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.15 }}
                    className="flex items-center justify-between p-3 bg-surface rounded-2xl" data-testid={`demo-settlement-${idx}`}>
                    <div className="flex items-center gap-2">
                      <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${s.from}`} alt={s.from} className="w-8 h-8 rounded-full" />
                      <div className="text-xs">
                        <span className="font-semibold text-slate-900">{s.from}</span>
                        <span className="text-slate-400 mx-1">bayar ke</span>
                        <span className="font-semibold text-slate-900">{s.to}</span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-danger">{fmt(s.amount)}</span>
                  </motion.div>
                )) : (
                  <p className="text-center text-slate-500 text-sm py-4">Semua sudah lunas!</p>
                )}
              </div>
              <button onClick={prevStep} className="w-full bg-surface text-slate-600 py-3 rounded-full font-semibold hover:bg-surface-hover transition-all">Kembali</button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* CTA - After Live Demo */}
      <div className="max-w-md mx-auto px-6 mb-16">
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          onClick={() => navigate('/register')}
          data-testid="cta-register-btn"
          className="w-full bg-primary text-white py-4 rounded-full font-bold text-lg hover:bg-primary-hover active:scale-95 transition-all shadow-float flex items-center justify-center gap-2"
        >
          Mulai Patungin Sekarang
          <ArrowRightIcon className="w-5 h-5" />
        </motion.button>
        <p className="text-center text-sm text-slate-500 mt-4">
          Sudah punya akun?{' '}
          <button onClick={() => navigate('/login')} data-testid="login-link" className="text-primary font-semibold hover:underline">
            Login di sini
          </button>
        </p>
      </div>

      {/* Features Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div key={idx} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 + idx * 0.1 }}
                className="bg-surface p-6 rounded-3xl text-center hover:shadow-soft transition-shadow">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-8">
        <p className="text-xs text-slate-400">Patungin &mdash; Patungan gak pake pusing.</p>
      </div>
    </div>
  );
};

export default Landing;
